import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { today, toStringDate, useDateShape } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import type {
	EPetitionDataRequest,
	EPetitionFilterForm,
} from '../seznam/context.types';

export interface FormInstanceProps {
	code: PageCode;
	defaultValues: EPetitionFilterForm;
}

export function FormInstance({
	code,
	defaultValues,
}: FormInstanceProps): FormDefinition<EPetitionFilterForm> {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const { getDatePickerShape } = useDateShape();
	const tsn = getLocalizeCurried(code);
	const formSchema = yup
		.object({
			text: yup.string().optional(),
			platnostOd: getDatePickerShape({
				isInvalidMessage: tsn('formular.validace.zadejte-datum-od'),
			})
				.max(today(), tsn('formular.validace.maximum-datum-od'))
				.nullable(),
			platnostDo: getDatePickerShape({
				isInvalidMessage: tsn('formular.validace.zadejte-datum-do'),
			})
				.nullable()
				.max(today(), tsn('formular.validace.maximum-datum-do'))
				.min(
					yup.ref('platnostOd'),
					tsn('formular.validace.starsi-nez-datum-od')
				),
		})
		.required();

	const formMethods = useGovForm<EPetitionFilterForm>({
		formSchema,
		defaultValues,
	});

	return getFormDefinition({ formMethods, formSchema });
}

export const prepareSubmitData = (
	formData: EPetitionFilterForm
): EPetitionDataRequest => ({
	params: {
		text: formData.text ?? undefined,
		platnostOd: formData.platnostOd ? toStringDate(formData.platnostOd) : null,
		platnostDo: formData.platnostDo ? toStringDate(formData.platnostDo) : null,
	},
});
